import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { Loader2, RefreshCw, Type, Upload } from 'lucide-preact'
import { useAuth } from '@/auth/useAuth'
import { useWebLoader } from '@/hooks/useWebLoader'
import { useLandingPageChat } from '@/hooks/useLandingPageChat'
import { useDebouncedPatch } from '@/hooks/useDebouncedPatch'
import { useToast } from '@/hooks/useToast'
import { landingPagesApi } from '@/api/landingPages'
import type { LandingPageLayout, LandingPageGenerateRequestDto, LandingPageContextDto, LandingPageViewDto, ThankYouPageSettingsDto, PageMetaDto, LandingPageContentImageDto, LandingPageTemplateListItemDto } from '@/types/landingPage'
import type { WebsterAttachment } from '@/types/webster'

import { LandingPageSidebar } from '@/components/webs/landingPage/LandingPageSidebar'
import { TemplatePicker } from '@/components/webs/landingPage/TemplatePicker'
import { compatibleWebsLabel } from '@/components/landingPageTemplates/templateText'
import { detectFontDirective } from '@/utils/fontDirective'
import { thankYouUrlError, firstThankYouUrlError, withheldThankYouFields } from '@/utils/landingPageUrl'
import { LandingPageEditorPanel } from '@/components/webs/landingPage/LandingPageEditorPanel'
import { HtmlPreview } from '@/components/webs/landingPage/HtmlPreview'
import { GenerationFailurePanel, FAILURE_PRIMARY_BUTTON, FAILURE_SECONDARY_BUTTON } from '@/components/webs/landingPage/GenerationFailurePanel'
import type { MissingFontRecovery } from '@/components/webs/landingPage/FontsSection'
import { takeFontFile } from '@/utils/fontFile'
import { holdsCompanyAdminRole } from '@/components/console/admin/useCompanyAdminAccess'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { describeGenerationFailure } from '@/utils/generationFailure'
import { routes } from '@/utils/routes'
import { isOnlineEventType } from '@/components/webs/constants/webType'
import { isOnDemand } from '@/components/console/campaigns/webHomeModel'
import { WebsterChatInput } from '@/components/webster/WebsterChatInput'
import { useGenerationEffort } from '@/components/webster/generationEffort'
import { ExternalModeNotice } from '@/components/webs/overlays/WebsterBuilderShared'

/** The named fields of `source`, for merging a few kept values over a fuller object. */
function pickFields<T extends object>(source: T | null, fields: string[]): Partial<T> {
    const from = (source ?? {}) as Record<string, unknown>
    return Object.fromEntries(fields.map(field => [field, from[field]])) as Partial<T>
}

export interface LandingPageBuilderProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
    assetId: string
    onPromoteDraft: (options?: { versionComment?: string }) => Promise<boolean>
    promoting: boolean
    onReadyForVersionChange?: (ready: boolean) => void
    /** Fires when the LP entity becomes available (post-generation). Lets the
     *  parent re-fetch the WebAsset so its LandingPageId / status are fresh. */
    onLandingPageEstablished?: () => void
    /** Fires after each chat-driven rebuild completes. Same parent action as
     *  onLandingPageEstablished — re-fetch the WebAsset — but on every edit
     *  so DraftUpdatedAt stays current and the Save-as-Version button can
     *  re-enable when the draft moves ahead of the latest version. Return
     *  value is ignored; if a Promise is returned it is awaited. */
    onLandingPageRebuilt?: () => unknown
    /** Reports the LP entity's DraftUpdatedAt to the parent. For LPs this is
     *  the authoritative draft timestamp (WebAsset.DraftUpdatedAt is not
     *  maintained server-side for LPs — the LP entity owns it). */
    onDraftUpdatedAtChange?: (value: string | null) => void
    /** Reports the generating state so the parent can disable Save-as-Version while
     *  Webster is working. For LPs the chat hook's `generating` flag already spans both
     *  the initial generation and chat-driven rebuilds, so one flag covers both. */
    onGeneratingChange?: (value: boolean) => void
}

export function LandingPageBuilder({
    clientCompanyId,
    workspaceId,
    campaignId,
    webId,
    assetId,
    onReadyForVersionChange,
    onLandingPageEstablished,
    onLandingPageRebuilt,
    onDraftUpdatedAtChange,
    onGeneratingChange,
}: LandingPageBuilderProps) {
    const { web } = useWebLoader(workspaceId, webId)
    const { userProfile, userCompanyRoleSets } = useAuth()
    const { canEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)
    const isCompanyAdmin = holdsCompanyAdminRole(userCompanyRoleSets, clientCompanyId)
    const { showError } = useToast()
    const generationEffort = useGenerationEffort()

    // Two different questions, deliberately not merged (ORB-826):
    //
    // `hasEventStates` decides whether the pages have a before and an after. The type comes first,
    // because the same rule has to hold in the sidebar's fields and in the API's prompt — and the API
    // checks the type first. A stray OnlineEvent payload under another type would otherwise give the
    // page a pre/post toggle with no post-event field behind it and a regeneration that ignores the
    // setting. An On-demand recording never airs, so it has no states either (ORB-1076).
    const isEventWeb = isOnlineEventType(web?.Type)

    // `hasEventContent` decides whether the agenda / speakers / countdown display toggles default on.
    // That predates this ticket and keeps the payload fallback it was written with: those sections are
    // driven by data that is either present or not, so a web carrying it should show it.
    const hasEventContent = !!web && (isOnlineEventType(web.Type) || !!web.OnlineEvent)

    // An On-demand recording has no start or end, so the countdown and duration settings can never take effect
    // (ORB-1030). Type first, as the API checks it.
    const onDemandEvent = isEventWeb && !!web?.OnlineEvent && isOnDemand(web.OnlineEvent)
    const hasEventStates = isEventWeb && !onDemandEvent

    const {
        messages, generating, hasGenerated,
        landingPage, setLandingPage, html, loading,
        context, contextLoading,
        thankYouHtml, thankYouGenerating, loadThankYouDraft, updateThankYouSettings,
        regenerateThankYou, suggestThankYouGeneration, canGenerateThankYou,
        chatReady,
        generationError, generationFailure, thankYouGenerationError, thankYouGenerationFailure,
        startGeneration, sendMessage, retryGeneration, rebuildWithCurrentBrand, loadPreview,
        updateSettings, updateContext, updatePageMeta, applyFont, applyFontFromChat,
        turnState, turnUserId, turnUserName,
        openDesignRound,
    } = useLandingPageChat({ clientCompanyId, workspaceId, campaignId, webId, onRebuilt: onLandingPageRebuilt })

    // Multi-user turn lock. When another user is mid-turn on the shared chat
    // session, lock this user's input and attribute the wait. Falls through to
    // null when the turn is idle or this user is the holder (their own input is
    // already disabled by `generating`).
    // Fallback to a generic label if turnUserName is missing — without it the
    // input wouldn't lock at all (lockedByUserName goes null), defeating the
    // turn-lock UX when the holder's name happens to be empty on the server side.
    const lockedByUserName = (turnState === 'Active' && turnUserId && turnUserId !== userProfile?.Id)
        ? (turnUserName || 'Another user')
        : null

    // Local settings state
    const [selectedLayout, setSelectedLayout] = useState<LandingPageLayout>(
        landingPage?.Settings?.Layout || 'Hero Flow'
    )
    const [showAgenda, setShowAgenda] = useState(landingPage?.Settings?.ShowAgenda ?? true)
    const [showSpeakerBios, setShowSpeakerBios] = useState(landingPage?.Settings?.ShowSpeakerBios ?? true)
    const [showCountdown, setShowCountdown] = useState(landingPage?.Settings?.ShowCountdown ?? true)
    const [showDuration, setShowDuration] = useState(landingPage?.Settings?.ShowDuration ?? true)
    // Always defaults true — independent of company-level TrackingEnabled (that
    // gate is enforced server-side at event ingestion, not at LP generation).
    const [includeTracking, setIncludeTracking] = useState(landingPage?.Settings?.IncludeTracking ?? true)
    // Per-LP opt-in (default false). Set in the setup sidebar, carried in the generate
    // request, and baked into the first version at generation — same as Layout. Read-only
    // in the post-gen edit panel; changed afterward via the Settings PATCH + a regenerate.
    const [includeCustomScript, setIncludeCustomScript] = useState(landingPage?.Settings?.IncludeCustomScript ?? false)
    const [formFields, setFormFields] = useState<string[] | null>(landingPage?.Settings?.FormFields ?? null)
    // Per-page font override (Secondary = heading, Primary = body).
    const [fontHeading, setFontHeading] = useState<string | undefined>(landingPage?.Settings?.FontOverrideSecondary ?? undefined)
    const [fontBody, setFontBody] = useState<string | undefined>(landingPage?.Settings?.FontOverridePrimary ?? undefined)

    // Start from a template (ORB-1103), chosen before the first build. The form fields start as the template's and the
    // ones on screen before it was picked come back with Start fresh. Only the newest pick may set the fields, so a slow
    // read for an earlier pick cannot land over a later one.
    const [startTemplate, setStartTemplate] = useState<LandingPageTemplateListItemDto | null>(null)
    const [templateFieldsLoading, setTemplateFieldsLoading] = useState(false)
    const freshFormFieldsRef = useRef<string[] | null>(null)
    const templatePickRef = useRef(0)

    const chooseStartTemplate = useCallback(async (template: LandingPageTemplateListItemDto | null) => {
        const pick = ++templatePickRef.current
        if (!template) {
            if (startTemplate) setFormFields(freshFormFieldsRef.current)
            setStartTemplate(null)
            setTemplateFieldsLoading(false)
            return
        }
        if (!startTemplate) freshFormFieldsRef.current = formFields
        setStartTemplate(template)
        setTemplateFieldsLoading(true)
        const res = await landingPagesApi.getLandingPage(workspaceId, template.WebId)
        if (templatePickRef.current !== pick) return
        setTemplateFieldsLoading(false)
        if (res.data) setFormFields(res.data.Settings.FormFields ?? null)
        else showError('Could not read the template’s form fields', 'The form fields on screen are kept. Check them before generating.')
    }, [startTemplate, formFields, workspaceId, showError])

    const [showPreview, setShowPreview] = useState(!!landingPage)
    useEffect(() => {
        if (generating || hasGenerated) setShowPreview(true)
    }, [generating, hasGenerated])

    const [localContext, setLocalContext] = useState<LandingPageContextDto | null>(null)
    const [localThankYouSettings, setLocalThankYouSettings] = useState<ThankYouPageSettingsDto | null>(null)
    const [localPageMeta, setLocalPageMeta] = useState<PageMetaDto | null>(null)
    const [localContentImages, setLocalContentImages] = useState<LandingPageContentImageDto[]>([])

    // The Context PATCH replaces the stored context wholesale — a field its payload omits is
    // erased, not left alone (LandingPageService.UpdateContextAsync). So a save has to carry the
    // whole context, and it has to be built from a value that is current *within* a burst of
    // keystrokes, not whatever the last render closed over. Hence a ref, written synchronously
    // alongside the state.
    const localContextRef = useRef<LandingPageContextDto | null>(null)

    // Same reason as localContextRef above: the Thank You guards are read at moments that do not
    // wait for a render, so they need the value as it is now, not as the last render saw it.
    const localThankYouRef = useRef<ThankYouPageSettingsDto | null>(null)

    // The last settings this panel took from the server. What the user has changed is the
    // difference between this and the panel — which is the only way to tell an edit they made from
    // a value that was stored before them. See the adopt-guard below.
    const adoptedThankYouRef = useRef<ThankYouPageSettingsDto | null>(null)

    useEffect(() => { if (landingPage?.ContentImages) setLocalContentImages(landingPage.ContentImages) }, [landingPage?.ContentImages])

    useEffect(() => {
        if (landingPage?.Settings) {
            setSelectedLayout(landingPage.Settings.Layout)
            setShowAgenda(landingPage.Settings.ShowAgenda)
            setShowSpeakerBios(landingPage.Settings.ShowSpeakerBios)
            setShowCountdown(landingPage.Settings.ShowCountdown)
            setShowDuration(landingPage.Settings.ShowDuration)
            // Defensive ?? true for pre-feature LPs whose Settings doc
            // pre-dates the IncludeTracking field.
            setIncludeTracking(landingPage.Settings.IncludeTracking ?? true)
            setIncludeCustomScript(landingPage.Settings.IncludeCustomScript ?? false)
            setFormFields(landingPage.Settings.FormFields ?? null)
            setFontHeading(landingPage.Settings.FontOverrideSecondary ?? undefined)
            setFontBody(landingPage.Settings.FontOverridePrimary ?? undefined)
        }
    }, [landingPage?.Id])

    // A chat edit saves the form fields too (ORB-1104), and a toggle here PATCHes the whole list, so the panel takes each
    // new list the server holds or its next toggle would put the old one back. Not while its own save is queued: that
    // response can predate a later toggle.
    const formFieldSavesRef = useRef(0)
    const savedFormFieldsKey = landingPage?.Settings?.FormFields?.join(',') ?? null
    useEffect(() => {
        if (!landingPage?.Settings || formFieldSavesRef.current > 0) return
        setFormFields(landingPage.Settings.FormFields ?? null)
    }, [savedFormFieldsKey])

    useEffect(() => {
        if (web && !hasEventContent && !landingPage?.Settings) {
            setShowAgenda(false)
            setShowSpeakerBios(false)
            setShowCountdown(false)
            setShowDuration(false)
        }
    }, [web, landingPage?.Settings])

    // Fetches the draft the page already had when the builder opened, once. loadPreview is a new
    // function every render, so with it in the deps the draft was fetched on every render while the
    // HTML was missing. Offline each failure toasted, the toast re-rendered the builder, and it
    // fetched again — hundreds of requests until the tab froze (ORB-1000). A page parked on design
    // questions has no draft, so it has nothing to fetch. A build that finishes while the builder is
    // open fetches its own draft in useLandingPageChat, so once one starts this stands down.
    const previewHandledRef = useRef(false)
    const draftBlobPath = landingPage?.DraftBlobPath ?? null
    useEffect(() => {
        if (generating) previewHandledRef.current = true
        if (previewHandledRef.current || !draftBlobPath || html) return
        previewHandledRef.current = true
        loadPreview()
    }, [draftBlobPath, html, generating])

    // Report to parent whether LP is ready for "Save as Version"
    // Ready = has been generated AND thank you page is done (canGenerateThankYou === false)
    useEffect(() => {
        onReadyForVersionChange?.(hasGenerated && !canGenerateThankYou)
    }, [hasGenerated, canGenerateThankYou, onReadyForVersionChange])

    // Push LP-entity DraftUpdatedAt up to the parent — it owns the Save gate
    // and for LPs this is the authoritative draft timestamp.
    useEffect(() => {
        onDraftUpdatedAtChange?.(landingPage?.DraftUpdatedAt ?? null)
    }, [landingPage?.DraftUpdatedAt, onDraftUpdatedAtChange])

    // Push generating up so the Save-as-Version button can be disabled while Webster is
    // working. For LPs `generating` already spans the initial generation and chat rebuilds.
    useEffect(() => {
        onGeneratingChange?.(generating)
    }, [generating, onGeneratingChange])

    // Notify parent when the LP entity first becomes available so it can
    // re-fetch the WebAsset (its LandingPageId is denormalized on the asset
    // and is what gates the "Save as Version" button).
    useEffect(() => {
        if (landingPage?.Id) onLandingPageEstablished?.()
    }, [landingPage?.Id, onLandingPageEstablished])

    useEffect(() => {
        if (hasGenerated && !thankYouHtml && !generating && !thankYouGenerating && landingPage?.ThankYouGenerationStatus === 'Completed') {
            loadThankYouDraft()
        }
    }, [hasGenerated, thankYouHtml, generating, thankYouGenerating, loadThankYouDraft, landingPage?.ThankYouGenerationStatus])

    const handleSend = async (message: string, attachments?: WebsterAttachment[]) => {
        if (!hasGenerated) {
            // The panel is editable before generation too, and an edit made then issues no save —
            // it rides into the CreateAndGenerate request body instead. Refused rather than quietly
            // dropped: the value is the user's, and the field beside it already says what is wrong
            // with it. The toast is only here because the panel can be collapsed, so the red field
            // is not necessarily on screen.
            const invalidUrl = firstThankYouUrlError(localThankYouRef.current)
            if (invalidUrl) {
                showError('Check the Thank You page', `${invalidUrl}.`)
                return
            }

            // Flip to the rocket/preview surface immediately so the user gets visual
            // feedback the moment they click Submit. The useEffect below also sets this
            // once chat.generating propagates, but doing it synchronously here means we
            // don't depend on state-update timing or extension-init races inside the
            // RichTextBoxes still mounted in the setup sidebar.
            setShowPreview(true)
            // A refused start, such as a template removed since the list loaded, made no page, and the canvas's Retry
            // can't make one, so the setup panel comes back with every choice on it. The refusal has been shown.
            const returnToSetup = (started: boolean) => {
                if (!started && !landingPage) setShowPreview(false)
            }
            if (startTemplate) {
                // The template supplies the design, context, page meta and images; the server ignores those fields
                // when TemplateWebId is set, so they are not sent. What this web supplies is.
                const request: LandingPageGenerateRequestDto = {
                    Layout: selectedLayout,
                    ShowAgenda: showAgenda,
                    ShowSpeakerBios: showSpeakerBios,
                    ShowCountdown: showCountdown,
                    ShowDuration: showDuration,
                    IncludeTracking: includeTracking,
                    IncludeCustomScript: includeCustomScript,
                    FormFields: formFields ?? undefined,
                    ThankYouSettings: localThankYouSettings ?? undefined,
                    AssetId: assetId,
                    TemplateWebId: startTemplate.WebId,
                }
                returnToSetup(await startGeneration(request, message || undefined))
                return
            }
            const request: LandingPageGenerateRequestDto = {
                Layout: selectedLayout,
                ShowAgenda: showAgenda,
                ShowSpeakerBios: showSpeakerBios,
                ShowCountdown: showCountdown,
                ShowDuration: showDuration,
                IncludeTracking: includeTracking,
                IncludeCustomScript: includeCustomScript,
                FontOverrideSecondary: fontHeading,
                FontOverridePrimary: fontBody,
                Context: localContext ?? undefined,
                ThankYouSettings: localThankYouSettings ?? undefined,
                PageMeta: localPageMeta ?? undefined,
                ContentImages: localContentImages.length > 0 ? localContentImages : undefined,
                FormFields: formFields ?? undefined,
                AssetId: assetId,
            }
            returnToSetup(await startGeneration(request, message || undefined))
        } else if (message || (attachments && attachments.length > 0)) {
            // A clear, text-only font directive ("use Poppins") applies instantly via the
            // apply-font endpoint instead of spending an AI rebuild; everything else is
            // normal Webster chat.
            const fontDirective = message && !(attachments && attachments.length > 0)
                ? detectFontDirective(message)
                : null
            if (fontDirective) {
                setFontHeading(fontDirective.heading ?? undefined)
                setFontBody(fontDirective.body ?? undefined)
                await applyFontFromChat(message, fontDirective.heading, fontDirective.body)
                return
            }
            await sendMessage(message, attachments)
        }
    }

    const handleToggleSetting = (field: 'ShowAgenda' | 'ShowSpeakerBios' | 'ShowCountdown' | 'ShowDuration' | 'IncludeTracking' | 'IncludeCustomScript', value: boolean) => {
        switch (field) {
            case 'ShowAgenda': setShowAgenda(value); break
            case 'ShowSpeakerBios': setShowSpeakerBios(value); break
            case 'ShowCountdown': setShowCountdown(value); break
            case 'ShowDuration': setShowDuration(value); break
            case 'IncludeTracking': setIncludeTracking(value); break
            case 'IncludeCustomScript': setIncludeCustomScript(value); break
        }
        if (landingPage) updateSettings({ [field]: value })
    }

    const handleLayoutChange = (layout: LandingPageLayout) => {
        setSelectedLayout(layout)
        if (landingPage) updateSettings({ Layout: layout })
    }

    const handleFormFieldsChange = async (canonicalKeys: string[]) => {
        setFormFields(canonicalKeys)
        if (!landingPage) return
        formFieldSavesRef.current += 1
        try {
            await updateSettings({ FormFields: canonicalKeys })
        } finally {
            formFieldSavesRef.current -= 1
        }
    }

    // Effective fonts shown in the picker: the per-page override wins, else the
    // knowledge base brand's face (snapshotted onto the context).
    const effectiveHeadingFont = fontHeading ?? localContext?.BrandFontSecondary ?? undefined
    const effectiveBodyFont = fontBody ?? localContext?.BrandFontPrimary ?? undefined

    const handleApplyFont = useCallback(async (heading: string | null, body: string | null) => {
        // Only pin the role the user actually changed. If an incoming value equals the
        // current effective font (that role wasn't touched), keep its existing state — an
        // explicit override, or inheriting the company brand font — rather than freezing
        // the brand default into an override.
        const nextHeading = heading === (effectiveHeadingFont ?? null) ? (fontHeading ?? null) : heading
        const nextBody = body === (effectiveBodyFont ?? null) ? (fontBody ?? null) : body
        setFontHeading(nextHeading ?? undefined)
        setFontBody(nextBody ?? undefined)
        // Post-generation: re-bake the draft instantly. Pre-generation: the override
        // rides into the Generate request body (handleSend) instead.
        if (landingPage) await applyFont(nextHeading, nextBody)
    }, [landingPage, applyFont, effectiveHeadingFont, effectiveBodyFont, fontHeading, fontBody])

    // Post-generation only: ask Webster to tune type scale/spacing for the current fonts
    // (a targeted edit via the normal chat flow — preserves copy/layout/colors).
    const handleRefineFont = useCallback(() => {
        void sendMessage('Refine the typography to suit the current fonts — adjust the type scale, line-height, letter-spacing, and font weights for balance and readability. Do not change any copy, layout, colors, or images.')
    }, [sendMessage])

    // ---- A build that failed, and the ways out of it (ORB-1040) ------------------------------
    // The server decides why it failed; this decides who may act on it. Every action here calls an
    // IsWorkspaceEditor endpoint, so a viewer gets the reason and who to ask instead of buttons.
    const failureView = generationFailure ? describeGenerationFailure(generationFailure) : null
    const missingFont = failureView?.font ?? null
    const [fontResolvedWith, setFontResolvedWith] = useState<string | null>(null)
    const [typographyRequest, setTypographyRequest] = useState(0)
    const canvasFontInputRef = useRef<HTMLInputElement>(null)

    // A new failure starts over: what fixed the last one says nothing about this one.
    useEffect(() => {
        setFontResolvedWith(null)
    }, [missingFont?.family, generationFailure?.Reference])
    // A failure can repeat with no reference to tell it apart, so a new build also starts over.
    useEffect(() => {
        if (generating) setFontResolvedWith(null)
    }, [generating])

    const uploadMissingFont = useCallback(async (file: File): Promise<boolean> => {
        if (!missingFont) return false
        // Registered under the brand's family, not the file's name, or the next build still can't find it.
        const res = await landingPagesApi.uploadFont(workspaceId, webId, missingFont.family, file)
        if (!res.data) {
            showError('Upload failed', res.error || 'Could not upload the font.')
            return false
        }
        setFontResolvedWith(missingFont.family)
        return true
    }, [missingFont, workspaceId, webId, showError])

    const replaceMissingFont = useCallback(async (replacement: string): Promise<boolean> => {
        // Both roles in one call: applying them one at a time re-resolves the other, still-missing font and fails.
        const applied = await applyFont(replacement, replacement)
        if (!applied) return false
        setFontHeading(replacement)
        setFontBody(replacement)
        setFontResolvedWith(replacement)
        return true
    }, [applyFont])

    // A replacement chosen before a reload is still on the page's settings, so it reads as fixed; an upload
    // leaves no such trace and shows the failure again until the next build, which then finds the file.
    const savedReplacement = missingFont && fontHeading && fontHeading === fontBody
        && fontHeading.toLowerCase() !== missingFont.family.toLowerCase() ? fontHeading : null
    const resolvedFont = fontResolvedWith ?? savedReplacement

    const fontRecovery: MissingFontRecovery | undefined = missingFont ? {
        family: missingFont.family,
        fromBrandGuidelines: missingFont.fromBrandGuidelines,
        resolvedWith: resolvedFont,
        canEdit,
        focusRequest: typographyRequest,
        onUpload: uploadMissingFont,
        onReplace: replaceMissingFont,
    } : undefined

    const pickCanvasFontFile = async (e: Event) => {
        const file = takeFontFile(e, showError)
        if (file) await uploadMissingFont(file)
    }

    const retryButton = (label: string) => (
        <button type="button" onClick={() => void retryGeneration()} disabled={generating} aria-busy={generating} className={FAILURE_PRIMARY_BUTTON}>
            <RefreshCw className="w-4 h-4" />
            {generating ? 'Starting…' : label}
        </button>
    )

    // One description of the failure, shown as the canvas panel on a page with nothing built yet and as
    // a strip above the page when a rebuild of an existing page fails, so the page stays in view.
    const renderFailure = (variant: 'panel' | 'banner'): ComponentChildren => {
        let content: ComponentChildren = null
        if (failureView && missingFont && resolvedFont) {
            const uploaded = resolvedFont === missingFont.family
            content = (
                <GenerationFailurePanel
                    variant={variant}
                    view={{
                        ...failureView,
                        kind: 'ready',
                        title: uploaded ? `${missingFont.family} is ready` : `${resolvedFont} will be used on this page`,
                        message: variant === 'banner'
                            ? `The page below now uses ${resolvedFont}.`
                            : uploaded
                            ? 'Webster will build this page with the font you uploaded.'
                            : `Webster will build this page in ${resolvedFont}. ${missingFont.fromBrandGuidelines ? `Your Brand Guidelines still say ${missingFont.family}.` : ''}`.trim(),
                        reference: null,
                    }}
                    actions={canEdit && variant === 'panel' ? retryButton('Generate page') : undefined}
                    footnote={uploaded ? <span>Only one weight was uploaded, so bold text uses a simulated bold.</span> : undefined}
                    viewerNote={canEdit ? null : 'A workspace editor can generate the page.'}
                />
            )
        } else if (failureView && missingFont) {
            content = (
                <GenerationFailurePanel
                    variant={variant}
                    view={failureView}
                    actions={canEdit ? (
                        <>
                            {/* Choosing comes first: most customers have no licensed font file, and none should need one to get a page. */}
                            <button type="button" disabled={generating} onClick={() => setTypographyRequest(request => request + 1)} className={FAILURE_PRIMARY_BUTTON}>
                                <Type className="w-4 h-4" />
                                Choose a font for this page
                            </button>
                            <input ref={canvasFontInputRef} type="file" accept=".woff2,font/woff2" className="hidden" onChange={pickCanvasFontFile} />
                            <button type="button" disabled={generating} onClick={() => canvasFontInputRef.current?.click()} className={FAILURE_SECONDARY_BUTTON}>
                                <Upload className="w-4 h-4" />
                                Upload {missingFont.family} (.woff2)
                            </button>
                        </>
                    ) : undefined}
                    footnote={missingFont.fromBrandGuidelines ? (
                        <>
                            <span>{missingFont.family} is set in your Brand Guidelines, so every new page uses it.</span>
                            {isCompanyAdmin && (
                                <span>
                                    <a href={routes.settingsBrandGuidelines(clientCompanyId)} className="font-medium text-primary-700 dark:text-primary-400 underline underline-offset-2">
                                        Open Brand Guidelines
                                    </a>{' '}to change the brand font for every page.
                                </span>
                            )}
                        </>
                    ) : undefined}
                    viewerNote={canEdit ? null : `You can view this page but not change it. Ask a workspace editor to upload ${missingFont.family} or choose a font for this page.`}
                />
            )
        } else if (failureView) {
            content = (
                <GenerationFailurePanel
                    variant={variant}
                    view={failureView}
                    actions={failureView.offerRetry && canEdit && variant === 'panel' ? retryButton('Retry') : undefined}
                    viewerNote={variant === 'banner'
                        ? (failureView.offerRetry ? 'To try again, send your change to Webster again.' : null)
                        : (failureView.offerRetry && !canEdit ? 'A workspace editor can retry the build.' : null)}
                />
            )
        }
        return content
    }
    const failureContent = renderFailure('panel')
    const failureBanner = html && !generating && landingPage?.GenerationStatus === 'Failed'
        ? renderFailure('banner')
        : null

    // Every sidebar field saves as you type, and each panel writes to the same landing-page
    // document under an optimistic-concurrency ETag. One request per keystroke meant only the
    // first in a burst could win — the rest were judged against an ETag it had already
    // superseded and came back conflicts, which the user saw as a wall of error toasts
    // (ORB-760). Edits now coalesce into one save per panel; the hook fills in the current
    // ETag as it sends, and queues saves so two panels cannot race each other.
    const { patch: patchContext, isSettling: contextSettling } =
        useDebouncedPatch<LandingPageContextDto>(updateContext)
    const { patch: patchThankYou, isSettling: thankYouSettling } =
        useDebouncedPatch<ThankYouPageSettingsDto>(updateThankYouSettings)
    const { patch: patchPageMeta, isSettling: pageMetaSettling } =
        useDebouncedPatch<PageMetaDto>(updatePageMeta)

    // ── Adopting a server value on top of a live edit ──
    //
    // Each of these three panels renders local state, adopted from whatever the server last
    // returned. Adopting on top of a live edit throws that edit away: the value arriving was
    // assembled before the user typed, so it is older than what is on screen — and a save's
    // response is the panel's own payload coming back, which makes the panel that just saved its
    // own worst source of stale values.
    //
    // "Live" is read from the two things that actually define it — an edit still waiting on the
    // debounce timer, and a save still in flight — rather than from a counter kept in step by
    // hand, which drifts the first time an edit takes a path that issues no save (a pre-generation
    // context edit rides into the Generate request body instead of saving).
    //
    // A value that arrives while an edit is live is DROPPED, not deferred: each effect is keyed on
    // what it adopts, and `isSettling` deliberately does not re-trigger it. Deferring would be the
    // wrong behaviour, not just a bigger change — the live edit's save writes the user's values, so
    // adopting the older snapshot afterwards would leave the panel showing something the server no
    // longer holds. Last writer wins, and the user is the last writer.
    //
    // Context was guarded first, because it is the panel whose PATCH replaces the whole document
    // and so turned a revert on screen into a revert in storage (ORB-760). Thank You and Page Meta
    // save one field at a time and only reverted on screen, which is why it took a tester typing a
    // URL slowly to see it — but it is the same defect, so they get the same guard.
    useEffect(() => {
        if (!context) return
        if (contextSettling()) return
        setLocalContext(context)
        localContextRef.current = context
    }, [context, contextSettling])

    // A URL the panel withheld from the server is unsaved, but it is still an edit. Nothing was
    // sent for it, so the debounce never saw it and `thankYouSettling()` is false — which is how a
    // half-typed URL came to be replaced by the stored value the moment any other panel saved.
    //
    // Which fields are withheld is read from the values themselves rather than flagged when a save
    // is skipped, so it cannot fall out of step with what is on screen, and through refs so that
    // correcting a URL does not re-run the effect and adopt over the correction.
    //
    // Two distinctions, each of which was a defect before it was drawn:
    //
    //  - Changed by the user, not merely invalid. Measured against the last settings the panel took
    //    from the server, because a page can hold a URL stored before the API validated these
    //    fields. Judging the panel by what it contains — or by how it differs from the newest
    //    response — reads that stored URL as a live edit and drops every update behind it.
    //  - Per field, not the whole object. One unsaveable URL is not a reason to ignore a change to
    //    the field beside it, and ignoring it has no end: nothing prompts a user to fix a URL they
    //    have abandoned, so the panel would simply stay stale.
    const thankYouWithheldFields = useCallback(
        () => withheldThankYouFields(localThankYouRef.current, adoptedThankYouRef.current),
        [],
    )

    useEffect(() => {
        const incoming = landingPage?.ThankYouSettings
        if (!incoming) return

        // A save on its way still means the whole response predates what the user typed, so nothing
        // is adopted while one is outstanding — that half stays all-or-nothing.
        if (thankYouSettling()) return

        // A withheld field is kept and the rest of the response is taken. Dropping the whole object
        // over one unsaveable URL left every other field stale for as long as the URL sat there,
        // which has no end: nothing prompts the user to fix a field they may have abandoned.
        //
        // The baseline advances to the whole response either way. A withheld field stays withheld
        // because the value the user typed still differs from what the server has just offered for
        // it — and if the two now agree, it was never theirs to protect.
        const withheld = thankYouWithheldFields()
        const next = withheld.length === 0
            ? incoming
            : { ...incoming, ...pickFields(localThankYouRef.current, withheld) }

        adoptedThankYouRef.current = incoming
        localThankYouRef.current = next
        setLocalThankYouSettings(next)
    }, [landingPage?.ThankYouSettings, thankYouSettling, thankYouWithheldFields])

    useEffect(() => {
        if (!landingPage?.PageMeta) return
        if (pageMetaSettling()) return
        setLocalPageMeta(landingPage.PageMeta)
    }, [landingPage?.PageMeta, pageMetaSettling])

    const handleContextChange = useCallback((field: keyof LandingPageContextDto, value: string | string[]) => {
        const next = {
            ...(localContextRef.current ?? { ReferenceUrls: [] as string[] }),
            [field]: value,
        } as LandingPageContextDto
        localContextRef.current = next
        setLocalContext(next)
        // Persist post-generation edits so a refresh doesn't lose the user's input.
        // Pre-generation, the value flows through the Generate request body so
        // there's nothing to save until landingPage exists.
        //
        // The whole context goes, not just `field` — see localContextRef above. Sending one
        // field nulled every other one, so editing the tagline threw away the tone of voice,
        // the brand colours, and the design direction the user built up answering Webster.
        if (landingPage) patchContext(next)
    }, [landingPage, patchContext])

    const handleThankYouSettingsChange = useCallback((field: keyof ThankYouPageSettingsDto, value: string) => {
        // Written synchronously alongside the state, because two guards read it at moments that do
        // not wait for a render: the adopt-effect below, and the generation check in handleSend.
        const next = { ...(localThankYouRef.current ?? {}), [field]: value } as ThankYouPageSettingsDto
        localThankYouRef.current = next
        setLocalThankYouSettings(next)
        if (!landingPage) return

        // A URL is invalid for most of the time it is being typed, and the API answers every
        // intermediate value 422. Asking the same rules here first means those keystrokes cost
        // nothing and say nothing: the field reports itself inline, and the save goes out when the
        // value becomes one the server will take — or when it is cleared (ORB-760).
        if (thankYouUrlError(field, value)) return

        patchThankYou({ [field]: value })
    }, [landingPage, patchThankYou])

    const handlePageMetaChange = useCallback((next: PageMetaDto) => {
        // Update local state synchronously so the controlled inputs in
        // PageMetaSection don't visibly revert while the PATCH is in flight.
        setLocalPageMeta(next)
        // Post-generation: persist immediately. Pre-generation: localPageMeta
        // is carried into the Generate request body so the values land on the
        // freshly-created LandingPage entity.
        if (landingPage) {
            patchPageMeta({
                OgImageAlt: next.OgImageAlt,
                MetaDescription: next.MetaDescription,
            })
        }
    }, [landingPage, patchPageMeta])

    const handleLandingPageUpdate = useCallback((updated: LandingPageViewDto) => {
        setLandingPage(updated)
    }, [setLandingPage])

    // Content images (ORB-413). Pre-generation, edits ride into the Generate request
    // body via localContentImages (images persist server-side at upload time; note
    // edits stay local until generation carries them into the manifest). Post-gen the
    // section is read-only/locked, so this only fires from the setup sidebar.
    const handleContentImagesChange = useCallback((next: LandingPageContentImageDto[]) => {
        setLocalContentImages(next)
    }, [])

    // Composer paperclip upload — stores the image and returns its URL for the
    // pending-attachment strip. Threaded into the post-gen editor panel's chat.
    const handleUploadImage = useCallback(async (file: File) => {
        const res = await landingPagesApi.uploadContentImage(workspaceId, webId, file)
        if (!res.data) throw new Error(res.error || 'Failed to upload image')
        // Post-generation the endpoint appends the image to the LP and returns the updated
        // entity — adopt it so the read-only Page Images list reflects the new chat-uploaded
        // image without a reload (the ContentImages effect cascades to localContentImages).
        if (res.data.LandingPage) setLandingPage(res.data.LandingPage)
        return {
            url: res.data.ContentImage.Url,
            fileName: res.data.ContentImage.FileName ?? undefined,
            contentType: res.data.ContentImage.ContentType ?? undefined,
        }
    }, [workspaceId, webId, setLandingPage])

    // Wait for all critical data before showing the builder, and for the draft HTML only when
    // there is a draft. A page waiting on design questions, or whose first build failed, has none,
    // and waiting for it spun forever.
    const isInitializing = loading || !web || (!!landingPage?.DraftBlobPath && !html && !generating)

    if (isInitializing) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">Loading...</span>
                </div>
            </div>
        )
    }

    if (landingPage?.Mode === 'External') {
        return <ExternalModeNotice url={landingPage.DeployedUrl} />
    }

    return (
        <div className="h-full flex overflow-hidden">
            {!showPreview ? (
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 overflow-y-auto">
                        <div className="max-w-3xl mx-auto">
                            {/* A web that runs a quiz can't start from a template (the server's UseRefusal), so none is offered. */}
                            {canEdit && !(web.Type === 'Download' && web.Download?.Quiz) && (
                                <div className="px-5 pt-5">
                                    <TemplatePicker
                                        workspaceId={workspaceId}
                                        webId={webId}
                                        compatibleLabel={compatibleWebsLabel(web.Type, web.OnlineEvent?.ModeType)}
                                        isOnlineEvent={isEventWeb}
                                        selected={startTemplate}
                                        onSelect={template => void chooseStartTemplate(template)}
                                        disabled={generating}
                                    />
                                    <div className="mt-5 border-t border-slate-200 dark:border-slate-700" />
                                </div>
                            )}
                            <LandingPageSidebar
                                context={localContext}
                                contextLoading={contextLoading}
                                selectedLayout={selectedLayout}
                                onSelectLayout={handleLayoutChange}
                                showAgenda={showAgenda}
                                showSpeakerBios={showSpeakerBios}
                                showCountdown={showCountdown}
                                showDuration={showDuration}
                                includeTracking={includeTracking}
                                includeCustomScript={includeCustomScript}
                                onToggleSetting={handleToggleSetting}
                                formFields={formFields}
                                onFormFieldsChange={handleFormFieldsChange}
                                onContextChange={handleContextChange}
                                thankYouSettings={localThankYouSettings}
                                onThankYouSettingsChange={handleThankYouSettingsChange}
                                pageMeta={localPageMeta}
                                onPageMetaChange={handlePageMetaChange}
                                onLandingPageUpdate={handleLandingPageUpdate}
                                workspaceId={workspaceId}
                                webId={webId}
                                headingFont={effectiveHeadingFont}
                                bodyFont={effectiveBodyFont}
                                onApplyFont={handleApplyFont}
                                onRefineFont={landingPage ? handleRefineFont : undefined}
                                contentImages={localContentImages}
                                onContentImagesChange={handleContentImagesChange}
                                disabled={generating}
                                webType={web.Type}
                                isOnDemand={onDemandEvent}
                                quiz={web.Download?.Quiz ?? null}
                                fromTemplate={!!startTemplate}
                            />
                        </div>
                    </div>

                    <WebsterChatInput
                        onSend={handleSend}
                        disabled={generating || templateFieldsLoading}
                        lockedByUserName={lockedByUserName}
                        buttonLabel={startTemplate ? 'Generate from template' : 'Generate'}
                        allowEmpty
                        placeholder={startTemplate
                            ? 'Anything to change from the template? For example: make the register button say Save my seat'
                            : 'Describe your ideal landing page, or just click Generate to get started...'}
                        generationEffort={generationEffort}
                    />
                </div>
            ) : (
                <>
                    <div className="flex-1 min-w-0">
                        <HtmlPreview
                            html={html}
                            thankYouHtml={thankYouHtml}
                            loading={generating && !html && !thankYouGenerating}
                            thankYouGenerating={thankYouGenerating}
                            generationError={generationError}
                            failureContent={failureContent}
                            failureBanner={failureBanner}
                            thankYouGenerationError={thankYouGenerationError}
                            thankYouGenerationFailure={thankYouGenerationFailure}
                            onRetry={retryGeneration}
                            canGenerateThankYou={canGenerateThankYou}
                            onGenerateThankYou={() => regenerateThankYou()}
                            // Generate copies a template's thank-you page again (ORB-1103); on any other page it designs one
                            // from nothing, a retry only when no thank-you page was there to lose.
                            canRetryThankYou={canEdit && landingPage?.ThankYouGenerationStatus === 'Failed'
                                && (!!landingPage.StartedFromTemplate || !landingPage.ThankYouDraftBlobPath)}
                            hasEventStates={hasEventStates}
                            quiz={web.Download?.Quiz ?? null}
                            // Outstanding questions freeze the bars on their own rather
                            // than waiting for the refetched status. The questions arriving
                            // IS the signal; making the freeze depend on a round-trip left
                            // the rocket visibly climbing while Webster was already asking.
                            awaitingDesignInput={
                                openDesignRound !== null
                                || landingPage?.GenerationStatus === 'AwaitingInput'
                            }
                        />
                    </div>

                    <LandingPageEditorPanel
                        context={localContext}
                        contextLoading={contextLoading}
                        selectedLayout={selectedLayout}
                        showAgenda={showAgenda}
                        showSpeakerBios={showSpeakerBios}
                        showCountdown={showCountdown}
                        showDuration={showDuration}
                        includeTracking={includeTracking}
                        includeCustomScript={includeCustomScript}
                        onSelectLayout={handleLayoutChange}
                        onToggleSetting={handleToggleSetting}
                        formFields={formFields}
                        onFormFieldsChange={handleFormFieldsChange}
                        onContextChange={handleContextChange}
                        thankYouSettings={localThankYouSettings}
                        onThankYouSettingsChange={handleThankYouSettingsChange}
                        pageMeta={localPageMeta}
                        onPageMetaChange={handlePageMetaChange}
                        contentImages={localContentImages}
                        chat={{ messages, generating, openDesignRound }}
                        onSend={handleSend}
                        onUploadImage={handleUploadImage}
                        disabled={generating}
                        chatDisabled={!chatReady}
                        lockedByUserName={lockedByUserName}
                        suggestThankYouGeneration={suggestThankYouGeneration}
                        onGenerateThankYou={() => regenerateThankYou()}
                        workspaceId={workspaceId}
                        webId={webId}
                        headingFont={effectiveHeadingFont}
                        bodyFont={effectiveBodyFont}
                        onApplyFont={handleApplyFont}
                        onRefineFont={landingPage ? handleRefineFont : undefined}
                        // A page started from a template carries the template's brand on purpose, and the API refuses
                        // to rebuild it from the knowledge base (ORB-1103), so the offer is not made.
                        onRebuildWithCurrentBrand={landingPage?.StartedFromTemplate ? undefined : rebuildWithCurrentBrand}
                        fontRecovery={fontRecovery}
                        openTypographyRequest={typographyRequest}
                        webType={web.Type}
                        isOnDemand={onDemandEvent}
                        quiz={web.Download?.Quiz ?? null}
                    />
                </>
            )}
        </div>
    )
}
